import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useLocation } from "react-router-dom";
import { buildSeoMeta } from "./seoMeta";
import { useAppSelector } from "store/hooks";
import { usePortalVanityUrl } from "store/slices/dashboardSlice";

const Helment = () => {
  const vanityUrl = usePortalVanityUrl();
  const DEFAULT_FAVICON = `${vanityUrl}favicon.ico`;
  const DEFAULT_APPLE_ICON = `${vanityUrl}dcdial.svg`;
  const [iconHref, setIconHref] = useState<string>(DEFAULT_FAVICON);
  const { favicon, companyDetails } = useAppSelector((state) => state.payment);
  const location = useLocation();
  const meta = buildSeoMeta(location?.pathname || "", companyDetails);

  const isSvgIcon = iconHref.toLowerCase().includes(".svg");
  const isPngIcon = iconHref.toLowerCase().includes(".png");
  const iconType = isSvgIcon
    ? "image/svg+xml"
    : isPngIcon
      ? "image/png"
      : "image/x-icon";
  const appleTouchIconHref =
    iconHref === DEFAULT_FAVICON ? DEFAULT_APPLE_ICON : iconHref;

  useEffect(() => {
    setIconHref(favicon?.trim() || DEFAULT_FAVICON);
  }, [favicon, DEFAULT_FAVICON]);

  const handleIconLoadError = () => {
    if (iconHref === DEFAULT_FAVICON) {
      return;
    }
    setIconHref(DEFAULT_FAVICON);
  };

  // og:* tags help browsers and crawlers that run JavaScript. Link previews in
  // SMS and email apps read the HTML before the app runs, so they need these
  // tags added server-side as well.
  return (
    <Helmet>
      <meta charSet="utf-8" />
      <title>{meta.title}</title>
      <meta name="description" content={meta.description} />
      <meta name="robots" content={meta.robots} />
      <meta property="og:type" content="website" />
      <meta property="og:title" content={meta.title} />
      <meta property="og:description" content={meta.description} />
      {meta.siteName && (
        <meta property="og:site_name" content={meta.siteName} />
      )}
      <link
        rel="icon"
        type={iconType}
        href={iconHref}
        onError={handleIconLoadError}
      />
      <link
        rel="shortcut icon"
        type={iconType}
        href={iconHref}
        onError={handleIconLoadError}
      />
      <link rel="apple-touch-icon" href={appleTouchIconHref} />
    </Helmet>
  );
};

export default Helment;
